/**
 * Web-specific Chat Actions
 * These actions include WebSocket functionality for web
 * This file should only be imported in web app components
 *
 * Only socket-specific actions are kept here. All other actions should use chat-slice directly.
 */

// Use crypto.randomUUID() for web (available in modern browsers and Node.js)
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
import {
  type SendMessageRequest,
  type Message,
  getEnvironmentConfig,
  getChatToken,
} from '@repo/api';
import {
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  CONTENT_TYPES,
  type ContentType,
  type WebSocketStatus,
} from '@repo/utils';
import { getChatWebSocket } from './ChatWebSocket';
import type { AppGlobalState } from '@/providers/StoreProvider';

// Helper to get store instance - will be set by the chat provider
let storeInstance: (() => AppGlobalState) | null = null;

export const setStoreInstance = (getState: () => AppGlobalState) => {
  storeInstance = getState;
};

export const isStoreReady = (): boolean => {
  return storeInstance !== null;
};

const getStore = (): AppGlobalState => {
  if (!storeInstance) {
    throw new Error('Store instance not set. Make sure ChatProvider is mounted.');
  }
  return storeInstance();
};

/**
 * Send a chat message with optimistic update
 * @param storeGetter - Optional function to get store state (for when store instance isn't set yet)
 */
export const sendChatMessage = async (
  {
    chatId,
    unitSlug,
    contentType,
    contentInput,
    relatedBotMessageId,
  }: {
    chatId: string;
    unitSlug?: string;
    contentType: ContentType;
    contentInput: string | boolean;
    /** Set when replying to a specific bot message. */
    relatedBotMessageId?: string;
  },
  storeGetter?: () => AppGlobalState,
) => {
  // Use provided storeGetter if available, otherwise fall back to global instance
  const getStoreState = storeGetter || (() => getStore());
  const storeState = getStoreState();
  const chatSlice = storeState?.chat;

  if (!chatSlice) {
    throw new Error('Chat slice not found in store');
  }

  const tempId = generateId();
  const chatMessage: Message = {
    id: tempId,
    atCreated: new Date().toISOString(),
    contentMessage: contentInput,
    contentInput: String(contentInput),
    senderType: MESSAGE_TYPES.USER,
    contentType,
    status: MESSAGE_STATUS.SENDING,
  };

  // Show optimistic message using chat-slice directly
  chatSlice.setChatDetail({ messages: [chatMessage], append: true });

  const messageBody: SendMessageRequest = {
    chatId,
    ...(unitSlug && { unitSlug }),
    contentType,
    contentInput,
    ...(relatedBotMessageId ? { relatedBotMessageId } : {}),
  };

  try {
    const currentState = getStoreState();
    const wsService = getChatWebSocket();

    // Check if WebSocket is connected - use the latest state from storeGetter
    const isWsConnected = wsService.isConnected();
    const wsStatus = currentState.chat.wsStatus;
    const wsConnectedChatId = currentState.chat.wsConnectedChatId;

    if (!isWsConnected || wsStatus !== 'connected') {
      console.error('WebSocket connection check failed:', {
        isWsConnected,
        wsStatus,
        wsConnectedChatId,
        expectedChatId: chatId,
        wsReadyState: wsService.getStatus(),
      });
      throw new Error(
        `WebSocket is not connected. Status: ${wsStatus}, Connected: ${isWsConnected}, ChatId: ${wsConnectedChatId}`,
      );
    }

    // Verify we're connected to the correct chat
    if (wsConnectedChatId !== chatId) {
      console.error('WebSocket connected to different chat:', {
        wsConnectedChatId,
        expectedChatId: chatId,
      });
      throw new Error(
        `WebSocket connected to different chat. Expected: ${chatId}, Actual: ${wsConnectedChatId}`,
      );
    }

    // Send via WebSocket - response will come through WebSocket onMessage callback
    await wsService.sendMessage(messageBody);
    // The actual message will be received via WebSocket and will replace the tempId message
  } catch (err) {
    console.error('Failed to send chat message:', err);
    chatSlice.markMessageAsFailed(tempId);
    throw err;
  }
};

/**
 * Reply to a document request with the ids of files already uploaded to S3.
 *
 * Deliberately not sendChatMessage: an upload has no text to show
 * optimistically, and the local copy that adds would render as a second upload
 * card. The backend retracts the request card it sent, not one the client
 * fabricated, so that copy would outlive the flow. The card owns its own
 * progress and error state, and the stored file arrives as a document_view.
 */
export const sendDocumentUploadReply = async (
  {
    chatId,
    unitSlug,
    uploadIds,
    relatedBotMessageId,
  }: {
    chatId: string;
    unitSlug?: string;
    uploadIds: string[];
    /** The document request being answered. */
    relatedBotMessageId?: string;
  },
  storeGetter?: () => AppGlobalState,
) => {
  const getStoreState = storeGetter || (() => getStore());
  const chatSlice = getStoreState()?.chat;

  if (!chatSlice) {
    throw new Error('Chat slice not found in store');
  }

  const wsService = getChatWebSocket();

  if (!wsService.isConnected() || chatSlice.wsStatus !== 'connected') {
    throw new Error('Chat is not connected.');
  }

  if (chatSlice.wsConnectedChatId !== chatId) {
    throw new Error('Chat is connected to a different conversation.');
  }

  const messageBody: SendMessageRequest = {
    chatId,
    ...(unitSlug ? { unitSlug } : {}),
    contentType: CONTENT_TYPES.DOCUMENT_REQUEST,
    uploadIds,
    ...(relatedBotMessageId ? { relatedBotMessageId } : {}),
  };

  await wsService.sendMessage(messageBody);
};

/**
 * Send slot appointment message
 * @param storeGetter - Optional function to get store state (for when store instance isn't set yet)
 */
export const sendSlotAppointmentMessage = async (
  {
    chatId,
    unitSlug,
    selectedValue,
    relatedBotMessage,
  }: {
    chatId: string;
    unitSlug: string;
    selectedValue: string;
    relatedBotMessage: Message; // actual message that needs to get update
  },
  storeGetter?: () => AppGlobalState,
) => {
  // Use provided storeGetter if available, otherwise fall back to global instance
  const getStoreState = storeGetter || (() => getStore());
  const storeState = getStoreState();
  const chatSlice = storeState?.chat;

  if (!chatSlice) {
    throw new Error('Chat slice not found in store');
  }

  const originalId = relatedBotMessage?.id;

  // 1. Optimistically highlight the selected slot
  const updatedContentData = (
    relatedBotMessage?.contentData as Array<{
      value?: string;
      [key: string]: unknown;
    }>
  )?.map(slot => ({
    ...slot,
    isSelect: slot?.value === selectedValue,
  }));

  const optimisticUpdate: Message = {
    ...relatedBotMessage,
    contentData: updatedContentData,
    // Keep original status so the message can be found for replacement
    status: relatedBotMessage.status || MESSAGE_STATUS.SENT,
  };

  // Use chat-slice directly
  chatSlice.replaceMessage({ tempId: originalId, actual: optimisticUpdate });

  const messageBody: SendMessageRequest = {
    chatId,
    unitSlug,
    contentType: CONTENT_TYPES.MULTIPLE_CHOICE,
    contentInput: selectedValue,
    relatedBotMessageId: originalId,
  };

  try {
    const currentState = getStoreState();
    const wsService = getChatWebSocket();

    // Check if WebSocket is connected
    if (!wsService.isConnected() || currentState.chat.wsStatus !== 'connected') {
      throw new Error('WebSocket is not connected. Cannot send message.');
    }

    // Send via WebSocket - response will come through WebSocket onMessage callback
    await wsService.sendMessage(messageBody);
    // The actual message will be received via WebSocket and will replace the message
  } catch (err) {
    const failedUpdate: Message = {
      ...relatedBotMessage,
      contentData: updatedContentData,
      contentInput: selectedValue,
      status: MESSAGE_STATUS.FAILED,
    };

    const finalState = getStoreState();
    const finalChatSlice = finalState?.chat;
    if (finalChatSlice) {
      finalChatSlice.replaceMessage({ tempId: originalId, actual: failedUpdate });
    }
    throw err;
  }
};

/**
 * Connect WebSocket for a chat
 * For web, token should be obtained before calling this function
 * @param storeGetter - Optional function to get store state (for when store instance isn't set yet)
 */
export const connectWebSocket = async (
  chatId: string,
  unitSlug: string,
  token?: string,
  storeGetter?: () => AppGlobalState,
) => {
  // Use provided storeGetter if available, otherwise fall back to global instance
  const getStoreState = storeGetter || (() => getStore());
  const storeState = getStoreState();
  const chatSlice = storeState?.chat;

  if (!chatSlice) {
    throw new Error('Chat slice not found in store');
  }

  try {
    // Get chat token if not provided (fallback for backward compatibility)
    let chatToken = token;
    if (!chatToken) {
      const tokenResponse = await getChatToken();
      chatToken = tokenResponse.token;
      if (!chatToken) {
        throw new Error('No chat token received from API');
      }
    }

    const wsService = getChatWebSocket();
    const currentState = storeState;

    // Check websocket service status first
    const wsStatus = wsService.getStatus();

    // If already connected to this chat, don't reconnect
    if (currentState.chat.wsConnectedChatId === chatId && wsStatus === 'connected') {
      return;
    }

    // If connecting to this chat, wait
    if (currentState.chat.wsConnectedChatId === chatId && wsStatus === 'connecting') {
      return;
    }

    // Disconnect existing connection if connected to a different chat
    if (currentState.chat.wsConnectedChatId && currentState.chat.wsConnectedChatId !== chatId) {
      wsService.disconnect();
      // Wait a bit for disconnect to complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Build URL with unit_slug - token will be added in ChatWebSocket.connect
    const config = getEnvironmentConfig();
    const baseUrl = config.endpoints.websocketChat;
    // Use URL constructor to properly build the websocket URL (like old production code)
    const socketUrl = new URL('/chat', baseUrl);
    socketUrl.searchParams.set('unit_slug', unitSlug);
    socketUrl.searchParams.set('ui_version', '5');
    const url = socketUrl.toString();

    wsService.connect({
      url,
      token: chatToken,
      onMessage: (message: Message) => {
        // When a message is received via WebSocket, handle it
        const currentState = getStoreState();
        const existingMessages = currentState.chat.chatDetail;

        // Check if this is replacing a temporary message
        // Find any message with SENDING status that might be this one
        // Match by content and sender type (user messages we sent)
        const tempMessage = existingMessages.find((msg: Message) => {
          // Must be a sending message from user
          if (msg.status !== MESSAGE_STATUS.SENDING || msg.senderType !== MESSAGE_TYPES.USER) {
            return false;
          }

          // If incoming message is also from user, try to match
          if (message.senderType === MESSAGE_TYPES.USER) {
            // Match by content type first
            if (msg.contentType !== message.contentType) {
              return false;
            }

            // Match by content - be flexible with string comparison
            const msgContent = String(msg.contentMessage || msg.contentInput || '');
            const incomingContent = String(message.contentMessage || message.contentInput || '');

            // Try exact match first
            if (msgContent === incomingContent) {
              return true;
            }

            // Try matching just the text content (ignore whitespace differences)
            if (msgContent.trim() === incomingContent.trim()) {
              return true;
            }

            // For appointment slots, match by relatedBotMessageId if present
            if (
              message.contentType === CONTENT_TYPES.MULTIPLE_CHOICE &&
              message.relatedBotMessageId
            ) {
              // This is a response to a bot message, not replacing our own
              return false;
            }
          }

          return false;
        });

        if (tempMessage) {
          // Replace the temporary message with the actual one (our own message)
          chatSlice.replaceMessage({ tempId: tempMessage.id, actual: message });
        } else {
          // Check if this is an updated bot message (e.g., slot selection updated the bot message)
          if (message.senderType === MESSAGE_TYPES.BOT && message.id) {
            const existingBotMessage = existingMessages.find(
              (msg: Message) => msg.id === message.id && msg.senderType === MESSAGE_TYPES.BOT,
            );

            if (existingBotMessage) {
              // Merge the backend response with any optimistic updates (preserve isSelect flags)
              // The backend might not always include isSelect, so we preserve it from the existing message
              const existingContentData = existingBotMessage.contentData as Array<{
                isSelect?: boolean;
                value?: string;
                datetimeValue?: string;
                [key: string]: unknown;
              }>;
              const incomingContentData = message.contentData as Array<{
                isSelect?: boolean;
                value?: string;
                datetimeValue?: string;
                [key: string]: unknown;
              }>;

              // If both have contentData, merge the isSelect flags by matching slots by value
              if (Array.isArray(existingContentData) && Array.isArray(incomingContentData)) {
                const mergedContentData = incomingContentData.map(incomingSlot => {
                  // Find matching slot in existing data by value or datetimeValue
                  const existingSlot = existingContentData.find(
                    slot =>
                      (slot.value && slot.value === incomingSlot.value) ||
                      (slot.datetimeValue && slot.datetimeValue === incomingSlot.datetimeValue) ||
                      (slot.value && slot.value === incomingSlot.datetimeValue) ||
                      (slot.datetimeValue && slot.datetimeValue === incomingSlot.value),
                  );

                  // Use incoming's isSelect if it's explicitly set, otherwise preserve existing
                  const isSelect =
                    incomingSlot.isSelect !== undefined
                      ? incomingSlot.isSelect
                      : existingSlot?.isSelect || false;

                  return {
                    ...incomingSlot,
                    isSelect,
                  };
                });
                message = { ...message, contentData: mergedContentData };
              }

              // Replace the existing bot message with the updated one
              chatSlice.replaceMessage({ tempId: message.id, actual: message });
            } else {
              // It's a new message from the server/other party
              chatSlice.setChatDetail({ messages: [message], append: true });
            }
          } else {
            // It's a new message from the server/other party (agent, etc.)
            chatSlice.setChatDetail({ messages: [message], append: true });
          }

          // Send read receipt for all non-user messages (bot AND agent).
          // WebSocket service handles deduplication via sentReadReceipts set.
          wsService.sendMessageReadReceipt(message.id);
        }
      },
      onStatusChange: (status: WebSocketStatus) => {
        const currentState = getStoreState();
        const chatSlice = currentState?.chat;
        if (chatSlice) {
          chatSlice.setWebSocketStatus(status);
        }
      },
      onBotTyping: (isTyping: boolean) => {
        // Backend controls loading indicator via contentType: 'loading' messages
        const currentState = getStoreState();
        const chatSlice = currentState?.chat;
        if (chatSlice) {
          chatSlice.setPollingStatus(isTyping);
        }
      },
      onMessageDeleted: (messageId: string) => {
        const currentState = getStoreState();
        const chatSlice = currentState?.chat;
        if (chatSlice) {
          chatSlice.removeChatMessageById(messageId, { adjustTotalCount: true });
        }
      },
      onError: () => {},
    });

    const finalState = getStoreState();
    const finalChatSlice = finalState?.chat;
    if (finalChatSlice) {
      finalChatSlice.setWebSocketConnectedChat(chatId);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Whether the underlying socket is actually open right now. Unlike the store's
 * `wsStatus`, this reflects the live `WebSocket.readyState`, so callers can
 * detect a stale "connected" store value left behind by a previous session.
 */
export const isWebSocketConnected = (): boolean => {
  return getChatWebSocket().isConnected();
};

/**
 * Disconnect WebSocket
 */
export const disconnectWebSocket = () => {
  // Disconnect websocket service regardless of store state
  const wsService = getChatWebSocket();
  wsService.disconnect();

  // Only update store if it's available
  if (!storeInstance) {
    return;
  }

  try {
    const store = getStore();
    const chatSlice = store?.chat;

    if (chatSlice) {
      chatSlice.setWebSocketConnectedChat(null);
      chatSlice.setWebSocketStatus('disconnected');
    }
  } catch (error) {
    // Store not available, but websocket is already disconnected
    // This is fine - the cleanup still succeeded
    console.warn('Could not update store on disconnect:', error);
  }
};
